import { relations, sql } from 'drizzle-orm';
import {
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  varchar,
} from 'drizzle-orm/pg-core';
import { createInsertSchema } from 'drizzle-zod';
import { z } from 'zod';

// Session storage table.
export const sessions = pgTable(
  'sessions',
  {
    sid: varchar('sid').primaryKey(),
    sess: jsonb('sess').notNull(),
    expire: timestamp('expire').notNull(),
  },
  table => [index('IDX_session_expire').on(table.expire)]
);

// Enums
export const roleEnum = pgEnum('role', [
  'team_member',
  'manager',
  'pm',
  'gm',
  'hod',
  'dpm',
  'admin',
  'staff',
]);
export const taskStatusEnum = pgEnum('task_status', [
  'pending',
  'started',
  'working',
  'need_more_time',
  'need_help',
  'help_done',
  'pending_approval',
  'complete',
  'dropped',
  'reopened',
]);
// Recurrence
export const recurrenceFrequencyEnum = pgEnum('recurrence_frequency', [
  'daily',
  'monthly',
  'yearly',
]);

// User storage table with username/password authentication
export const users = pgTable('users', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  username: varchar('username').unique().notNull(),
  email: varchar('email').unique(),
  password: varchar('password').notNull(),
  firstName: varchar('first_name'),
  lastName: varchar('last_name'),
  profileImageUrl: varchar('profile_image_url'),
  role: roleEnum('role').default('team_member').notNull(),
  teamId: varchar('team_id'),
  cityId: varchar('city_id'), // Keep for backward compatibility
  cityIds: jsonb('city_ids'), // Array of city IDs for PMs: string[]
  allowTeamView: boolean('allow_team_view').default(false).notNull(),
  assignedByPmId: varchar('assigned_by_pm_id').references(() => users.id), // PM who assigned this staff
  assignedByHodId: varchar('assigned_by_hod_id').references(() => users.id), // HOD who assigned this staff
  isActive: boolean('is_active').default(true).notNull(),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

export const cities = pgTable('cities', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: varchar('name').notNull(),
  description: text('description'),
  gmId: varchar('gm_id').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

export const teams = pgTable('teams', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: varchar('name').notNull(),
  description: text('description'),
  managerId: varchar('manager_id'),
  pmId: varchar('pm_id'), // Keep for backward compatibility
  cityId: varchar('city_id'), // Single city per project
  pmIds: jsonb('pm_ids'), // Array of PM user IDs: string[]
  gmId: varchar('gm_id'), // GM included in this project (optional)
  hodId: varchar('hod_id'), // HOD included in this project (optional) - Keep for backward compatibility
  hodIds: jsonb('hod_ids'), // Array of HOD user IDs: string[]
  dpmId: varchar('dpm_id'), // DPM included in this project (optional) - backward compatibility
  dpmIds: jsonb('dpm_ids'), // Array of DPM user IDs: string[]
  createdAt: timestamp('created_at').defaultNow(),
});

// User-Team membership (additional teams for a user)
export const userTeams = pgTable(
  'user_teams',
  {
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    teamId: varchar('team_id')
      .references(() => teams.id)
      .notNull(),
    createdAt: timestamp('created_at').defaultNow(),
  },
  t => [
    // Composite primary key to prevent duplicates
    index('IDX_user_team').on(t.userId, t.teamId),
  ]
);

export const projects = pgTable('projects', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: varchar('name').notNull(),
  description: text('description'),
  color: varchar('color').default('#3B82F6'),
  hasAmc: boolean('has_amc').default(false).notNull(),
  amcExpiryDate: timestamp('amc_expiry_date'),
  supportPackageValidityPeriod: timestamp('support_package_validity_period'),
  teamId: varchar('team_id').references(() => teams.id),
  createdAt: timestamp('created_at').defaultNow(),
});

export const tasks = pgTable(
  'tasks',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    title: varchar('title').notNull(),
    description: text('description'),
    status: taskStatusEnum('status').default('pending').notNull(),
    assignedTo: varchar('assigned_to').references(() => users.id),
    // When true, this task is a shared/common task with multiple assignees
    // linked via the task_assignees table. All assignees see the same task
    // record and share status/progress.
    isCommon: boolean('is_common').default(false).notNull(),
    projectId: varchar('project_id').references(() => projects.id),
    teamId: varchar('team_id').references(() => teams.id), // optional team context for unassigned visibility
    dueDate: timestamp('due_date').notNull(),
    completedAt: timestamp('completed_at'),
    approvedBy: varchar('approved_by').references(() => users.id),
    approvedAt: timestamp('approved_at'),
    createdBy: varchar('created_by')
      .references(() => users.id)
      .notNull(),
    timeTracked: integer('time_tracked').default(0), // minutes tracked
    timeStarted: timestamp('time_started'), // when current time tracking session started
    estimatedHours: integer('estimated_hours'), // estimated hours for the task
    priority: integer('priority').default(0), // task priority/order for sorting
    // Recurrence fields
    isRecurring: boolean('is_recurring').default(false).notNull(),
    recurrenceFrequency: recurrenceFrequencyEnum('recurrence_frequency'),
    recurrenceNextRun: timestamp('recurrence_next_run'),
    recurrenceEndDate: timestamp('recurrence_end_date'),
    recurrenceOriginalId: varchar('recurrence_original_id').references(
      () => tasks.id
    ),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
    acknowledgedByCreatorAt: timestamp('acknowledged_by_creator_at'),
  },
  t => [
    index('IDX_tasks_assigned_to').on(t.assignedTo),
    index('IDX_tasks_project_id').on(t.projectId),
    index('IDX_tasks_team_id').on(t.teamId),
    index('IDX_tasks_status').on(t.status),
    index('IDX_tasks_created_at').on(t.createdAt),
    // Composite indexes to speed common filters
    index('IDX_tasks_team_status_created').on(t.teamId, t.status, t.createdAt),
    index('IDX_tasks_assignee_status_due').on(
      t.assignedTo,
      t.status,
      t.dueDate
    ),
  ]
);

// Time tracking entries table
export const timeEntries = pgTable('time_entries', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  taskId: varchar('task_id')
    .references(() => tasks.id)
    .notNull(),
  userId: varchar('user_id')
    .references(() => users.id)
    .notNull(),
  startTime: timestamp('start_time').notNull(),
  endTime: timestamp('end_time'),
  duration: integer('duration'), // minutes
  description: text('description'),
  createdAt: timestamp('created_at').defaultNow(),
});

// Task assignees table - supports multiple users sharing a single task
export const taskAssignees = pgTable(
  'task_assignees',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    taskId: varchar('task_id')
      .references(() => tasks.id)
      .notNull(),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp('created_at').defaultNow(),
  },
  t => [
    // Composite index to efficiently look up assignees for a task and
    // tasks for a given user.
    index('IDX_task_assignees_task_user').on(t.taskId, t.userId),
  ]
);

// Task history/audit log table
export const taskHistory = pgTable(
  'task_history',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    taskId: varchar('task_id')
      .references(() => tasks.id)
      .notNull(),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(), // who made the change
    action: varchar('action').notNull(), // 'created', 'assigned', 'status_changed', 'completed', 'approved', 'commented'
    details: text('details'), // JSON string with change details
    oldValue: text('old_value'), // previous value (for status changes, assignments, etc.)
    newValue: text('new_value'), // new value
    createdAt: timestamp('created_at').defaultNow(),
  },
  t => [
    index('IDX_task_history_task').on(t.taskId),
    index('IDX_task_history_created_at').on(t.createdAt),
  ]
);

// Task comments table
export const taskComments = pgTable(
  'task_comments',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    taskId: varchar('task_id')
      .references(() => tasks.id)
      .notNull(),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    content: text('content').notNull(),
    attachments: text('attachments').array(), // array of file URLs/paths
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  t => [
    index('IDX_task_comments_task').on(t.taskId),
    index('IDX_task_comments_created_at').on(t.createdAt),
  ]
);

// Task help/sharing table
export const taskHelp = pgTable('task_help', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  taskId: varchar('task_id')
    .references(() => tasks.id)
    .notNull(),
  requesterId: varchar('requester_id')
    .references(() => users.id)
    .notNull(), // who requested help
  helperId: varchar('helper_id')
    .references(() => users.id)
    .notNull(), // who can help
  message: text('message'), // optional message explaining what help is needed
  status: varchar('status').default('pending').notNull(), // pending, accepted, declined
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// General help requests table (for system admin help)
export const helpRequests = pgTable('help_requests', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  requesterId: varchar('requester_id')
    .references(() => users.id)
    .notNull(), // who requested help
  category: varchar('category').notNull(), // 'technical', 'account', 'feature', 'bug', 'other'
  subject: varchar('subject').notNull(), // brief subject line
  description: text('description').notNull(), // detailed description of the issue
  priority: varchar('priority').default('medium').notNull(), // 'low', 'medium', 'high', 'urgent'
  status: varchar('status').default('open').notNull(), // 'open', 'in_progress', 'resolved', 'closed'
  assignedTo: varchar('assigned_to').references(() => users.id), // admin assigned to handle this request
  resolution: text('resolution'), // admin's response/solution
  resolvedAt: timestamp('resolved_at'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Task extension requests table
export const taskExtensionRequests = pgTable('task_extension_requests', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  taskId: varchar('task_id')
    .references(() => tasks.id)
    .notNull(),
  requestedBy: varchar('requested_by')
    .references(() => users.id)
    .notNull(), // who requested the extension
  requestedDays: integer('requested_days').notNull(), // number of days requested
  reason: text('reason').notNull(), // reason for extension (from comment)
  commentId: varchar('comment_id').references(() => taskComments.id), // link to the comment
  status: varchar('status').default('pending').notNull(), // 'pending', 'approved', 'rejected'
  approvedBy: varchar('approved_by').references(() => users.id), // admin who approved/rejected
  approvedAt: timestamp('approved_at'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Task viewers table - stores users who can view tasks (e.g., GM as viewer)
export const taskViewers = pgTable(
  'task_viewers',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    taskId: varchar('task_id')
      .references(() => tasks.id)
      .notNull(),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp('created_at').defaultNow(),
  },
  t => [
    // Composite unique constraint to prevent duplicate viewer entries
    index('IDX_task_viewers_task_user').on(t.taskId, t.userId),
  ]
);

// Relations
export const usersRelations = relations(users, ({ one, many }) => ({
  team: one(teams, {
    fields: [users.teamId],
    references: [teams.id],
  }),
  city: one(cities, {
    fields: [users.cityId],
    references: [cities.id],
  }),
  extraTeams: many(userTeams),
  assignedTasks: many(tasks, { relationName: 'assignedTasks' }),
  createdTasks: many(tasks, { relationName: 'createdTasks' }),
  approvedTasks: many(tasks, { relationName: 'approvedTasks' }),
  managedTeam: one(teams, {
    fields: [users.id],
    references: [teams.managerId],
  }),
  gmCity: one(cities, {
    fields: [users.id],
    references: [cities.gmId],
  }),
}));

export const teamsRelations = relations(teams, ({ one, many }) => ({
  manager: one(users, {
    fields: [teams.managerId],
    references: [users.id],
  }),
  pm: one(users, {
    fields: [teams.pmId],
    references: [users.id],
  }),
  city: one(cities, {
    fields: [teams.cityId],
    references: [cities.id],
  }),
  members: many(users),
  extraMembers: many(userTeams),
}));

export const citiesRelations = relations(cities, ({ one, many }) => ({
  gm: one(users, {
    fields: [cities.gmId],
    references: [users.id],
  }),
  teams: many(teams),
  users: many(users),
}));

export const projectsRelations = relations(projects, ({ many }) => ({
  tasks: many(tasks),
}));

export const tasksRelations = relations(tasks, ({ one, many }) => ({
  assignee: one(users, {
    fields: [tasks.assignedTo],
    references: [users.id],
    relationName: 'assignedTasks',
  }),
  creator: one(users, {
    fields: [tasks.createdBy],
    references: [users.id],
    relationName: 'createdTasks',
  }),
  approver: one(users, {
    fields: [tasks.approvedBy],
    references: [users.id],
    relationName: 'approvedTasks',
  }),
  project: one(projects, {
    fields: [tasks.projectId],
    references: [projects.id],
  }),
  timeEntries: many(timeEntries),
  assignees: many(taskAssignees),
  history: many(taskHistory),
  comments: many(taskComments),
  helpRequests: many(taskHelp),
  extensionRequests: many(taskExtensionRequests),
}));

export const timeEntriesRelations = relations(timeEntries, ({ one }) => ({
  task: one(tasks, {
    fields: [timeEntries.taskId],
    references: [tasks.id],
  }),
  user: one(users, {
    fields: [timeEntries.userId],
    references: [users.id],
  }),
}));

export const taskHistoryRelations = relations(taskHistory, ({ one }) => ({
  task: one(tasks, {
    fields: [taskHistory.taskId],
    references: [tasks.id],
  }),
  user: one(users, {
    fields: [taskHistory.userId],
    references: [users.id],
  }),
}));

export const taskCommentsRelations = relations(taskComments, ({ one }) => ({
  task: one(tasks, {
    fields: [taskComments.taskId],
    references: [tasks.id],
  }),
  user: one(users, {
    fields: [taskComments.userId],
    references: [users.id],
  }),
}));

export const taskAssigneesRelations = relations(taskAssignees, ({ one }) => ({
  task: one(tasks, {
    fields: [taskAssignees.taskId],
    references: [tasks.id],
  }),
  user: one(users, {
    fields: [taskAssignees.userId],
    references: [users.id],
  }),
}));

export const taskHelpRelations = relations(taskHelp, ({ one }) => ({
  task: one(tasks, {
    fields: [taskHelp.taskId],
    references: [tasks.id],
  }),
  requester: one(users, {
    fields: [taskHelp.requesterId],
    references: [users.id],
  }),
  helper: one(users, {
    fields: [taskHelp.helperId],
    references: [users.id],
  }),
}));

export const helpRequestsRelations = relations(helpRequests, ({ one }) => ({
  requester: one(users, {
    fields: [helpRequests.requesterId],
    references: [users.id],
  }),
  assignedAdmin: one(users, {
    fields: [helpRequests.assignedTo],
    references: [users.id],
  }),
}));

export const taskExtensionRequestsRelations = relations(
  taskExtensionRequests,
  ({ one }) => ({
    task: one(tasks, {
      fields: [taskExtensionRequests.taskId],
      references: [tasks.id],
    }),
    requester: one(users, {
      fields: [taskExtensionRequests.requestedBy],
      references: [users.id],
    }),
    approver: one(users, {
      fields: [taskExtensionRequests.approvedBy],
      references: [users.id],
    }),
    comment: one(taskComments, {
      fields: [taskExtensionRequests.commentId],
      references: [taskComments.id],
    }),
  })
);

// Device tokens for push notifications
export const deviceTokens = pgTable(
  'device_tokens',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    token: varchar('token').notNull(),
    platform: varchar('platform').notNull(), // 'web', 'ios', 'android'
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  t => [
    index('IDX_device_tokens_user').on(t.userId),
    index('IDX_device_tokens_token').on(t.token),
  ]
);

// Notifications table
export const notifications = pgTable(
  'notifications',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    taskId: varchar('task_id')
      .references(() => tasks.id)
      .notNull(),
    type: varchar('type').notNull(), // 'comment', 'status_change', 'assignment', etc.
    title: varchar('title').notNull(),
    message: text('message').notNull(),
    isRead: boolean('is_read').default(false),
    createdAt: timestamp('created_at').defaultNow(),
  },
  t => [
    index('IDX_notifications_user').on(t.userId),
    index('IDX_notifications_task').on(t.taskId),
    index('IDX_notifications_created_at').on(t.createdAt),
    index('IDX_notifications_unread').on(t.userId, t.isRead),
  ]
);

export const notificationsRelations = relations(notifications, ({ one }) => ({
  user: one(users, {
    fields: [notifications.userId],
    references: [users.id],
  }),
  task: one(tasks, {
    fields: [notifications.taskId],
    references: [tasks.id],
  }),
}));

// App settings (key/value JSON)
export const appSettings = pgTable('app_settings', {
  key: varchar('key').primaryKey(),
  value: jsonb('value').notNull(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Score snapshots for trend charts
export const userScoreSnapshots = pgTable(
  'user_score_snapshots',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    windowDays: integer('window_days').notNull(),
    points: integer('points').notNull(),
    capturedAt: timestamp('captured_at').defaultNow(),
  },
  t => [
    index('IDX_score_snapshots_user_window').on(t.userId, t.windowDays),
    index('IDX_score_snapshots_captured').on(t.capturedAt),
  ]
);

// Insert schemas
export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertTeamSchema = createInsertSchema(teams).omit({
  id: true,
  createdAt: true,
});

export const insertProjectSchema = createInsertSchema(projects).omit({
  id: true,
  createdAt: true,
});

export const insertTaskSchema = createInsertSchema(tasks).omit({
  id: true,
  completedAt: true,
  approvedBy: true,
  approvedAt: true,
  createdAt: true,
  updatedAt: true,
});

export const updateTaskSchema = createInsertSchema(tasks)
  .omit({
    id: true,
    createdBy: true,
    createdAt: true,
    updatedAt: true, // Always set server-side to ensure correct timestamp
  })
  .partial();

export const insertTimeEntrySchema = createInsertSchema(timeEntries).omit({
  id: true,
  createdAt: true,
});

export const updateTimeEntrySchema = createInsertSchema(timeEntries)
  .omit({
    id: true,
    taskId: true,
    userId: true,
    createdAt: true,
  })
  .partial();

export const insertTaskHistorySchema = createInsertSchema(taskHistory).omit({
  id: true,
  createdAt: true,
});

export const insertTaskCommentSchema = createInsertSchema(taskComments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateTaskCommentSchema = createInsertSchema(taskComments)
  .omit({
    id: true,
    taskId: true,
    userId: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial();

export const insertTaskHelpSchema = createInsertSchema(taskHelp).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateTaskHelpSchema = createInsertSchema(taskHelp)
  .omit({
    id: true,
    taskId: true,
    requesterId: true,
    helperId: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial();

export const insertHelpRequestSchema = createInsertSchema(helpRequests).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateHelpRequestSchema = createInsertSchema(helpRequests)
  .omit({
    id: true,
    requesterId: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial();

export const insertTaskExtensionRequestSchema = createInsertSchema(
  taskExtensionRequests
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateTaskExtensionRequestSchema = createInsertSchema(
  taskExtensionRequests
)
  .omit({
    id: true,
    taskId: true,
    requestedBy: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial();

export const insertTaskViewerSchema = createInsertSchema(taskViewers).omit({
  id: true,
  createdAt: true,
});

export const insertTaskAssigneeSchema = createInsertSchema(taskAssignees).omit({
  id: true,
  createdAt: true,
});

export const insertDeviceTokenSchema = createInsertSchema(deviceTokens).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types
export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;

export type Team = typeof teams.$inferSelect;
export type InsertTeam = z.infer<typeof insertTeamSchema>;

export type Project = typeof projects.$inferSelect;
export type InsertProject = z.infer<typeof insertProjectSchema>;

export type Task = typeof tasks.$inferSelect;
export type InsertTask = z.infer<typeof insertTaskSchema>;
export type UpdateTask = z.infer<typeof updateTaskSchema>;

export type TimeEntry = typeof timeEntries.$inferSelect;
export type InsertTimeEntry = z.infer<typeof insertTimeEntrySchema>;
export type UpdateTimeEntry = z.infer<typeof updateTimeEntrySchema>;

export type TaskHistory = typeof taskHistory.$inferSelect;
export type InsertTaskHistory = z.infer<typeof insertTaskHistorySchema>;

export type TaskComment = typeof taskComments.$inferSelect;
export type InsertTaskComment = z.infer<typeof insertTaskCommentSchema>;
export type UpdateTaskComment = z.infer<typeof updateTaskCommentSchema>;

export type TaskHelp = typeof taskHelp.$inferSelect;
export type InsertTaskHelp = z.infer<typeof insertTaskHelpSchema>;
export type UpdateTaskHelp = z.infer<typeof updateTaskHelpSchema>;

export type HelpRequest = typeof helpRequests.$inferSelect;
export type InsertHelpRequest = z.infer<typeof insertHelpRequestSchema>;
export type UpdateHelpRequest = z.infer<typeof updateHelpRequestSchema>;

export type TaskExtensionRequest = typeof taskExtensionRequests.$inferSelect;
export type InsertTaskExtensionRequest = z.infer<
  typeof insertTaskExtensionRequestSchema
>;
export type UpdateTaskExtensionRequest = z.infer<
  typeof updateTaskExtensionRequestSchema
>;

export type TaskViewer = typeof taskViewers.$inferSelect;
export type InsertTaskViewer = z.infer<typeof insertTaskViewerSchema>;

export type TaskAssignee = typeof taskAssignees.$inferSelect;
export type InsertTaskAssignee = z.infer<typeof insertTaskAssigneeSchema>;

export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = typeof notifications.$inferInsert;

// Extended types with relations
export type UserWithTeam = User & {
  team?: Team;
};

export type TaskWithRelations = Task & {
  assignee?: User;
  creator?: User;
  approver?: User;
  project?: Project;
  timeEntries?: TimeEntry[];
  hasUnreadComments?: boolean;
};

export type TeamWithMembers = Team & {
  manager?: User;
  members?: User[];
};

export type TimeEntryWithUser = TimeEntry & {
  user?: User;
};

export type TaskHistoryWithUser = TaskHistory & {
  user?: User;
};

export type TaskCommentWithUser = TaskComment & {
  user?: User;
};

export type TaskHelpWithUsers = TaskHelp & {
  requester?: User;
  helper?: User;
  task?: Task;
};

export type HelpRequestWithUsers = HelpRequest & {
  requester?: User;
  assignedAdmin?: User;
};

export type DeviceToken = typeof deviceTokens.$inferSelect;
export type InsertDeviceToken = z.infer<typeof insertDeviceTokenSchema>;
export type AppSetting = typeof appSettings.$inferSelect;
